import { useEffect, useRef } from 'react';
import { Button } from '../../components/ui/button';

// Duplicate-row confirmation (P2b-2 §6.8). Fires from the master-grid
// Actions menu. Spec intent: a deliberate "make another row like this one"
// moment, so we ask once before creating — reversing a duplicate means
// hard-deleting a secondary row, which is a second Actions-menu step.
//
// Default focus on Cancel so muscle-memory Enter doesn't mint duplicates.
// Matches the centre-overlay convention used by OptOutConfirmModal and
// RefreshFromCampaignModal.

type Props = {
  locationName: string;
  onConfirm: () => void;
  onCancel: () => void;
  busy?: boolean;
};

export function DuplicateRowModal({ locationName, onConfirm, onCancel, busy }: Props) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div
        className="w-full max-w-lg rounded-md bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="duplicate-row-title"
      >
        <div className="p-6 space-y-4">
          <h2 id="duplicate-row-title" className="text-lg font-semibold text-slate-900">
            Duplicate row for {locationName}?
          </h2>
          <p className="text-sm text-slate-700">
            A new secondary row will be created under this location. The duplicate starts
            as <strong>unassigned</strong> — you'll pick a Campaign and author its content
            via the row editor.
          </p>
          <p className="text-sm text-slate-500">
            Dates, account manager, and row notes copy from this row. Budgets and content
            reset — secondary rows never hold budget, and content is authored per-campaign.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <button
              ref={cancelRef}
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <Button onClick={onConfirm} disabled={busy}>
              {busy ? 'Duplicating…' : 'Duplicate'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
